/**
 * Email Service (frontend)
 * Sends transactional emails through the server-side Resend function at
 * POST /api/send-email (Cloudflare Pages Function). The Resend API key is NOT
 * in the browser — the function holds it. This also avoids Resend's browser CORS
 * block (Resend refuses direct browser calls).
 */
import { apiHeadersOrThrow } from '../lib/authHeader';

export interface EmailResult {
  success: boolean;
  message: string;
  /* Set when the send failed because the endpoint is not there at all — an environment
     without the Pages Function, or without Resend configured. It is the only failure a
     caller should paper over with a fallback: a refusal (403, 404 on the address, a bad
     request) is an answer, and swallowing it sends the wrong email and hides the reason. */
  endpointMissing?: boolean;
  details?: {
    email?: string;
    subject?: string;
    email_id?: string;
  };
}

/** Attribution for email_deliveries: which deal, and which step sent it. */
export interface EmailTracking {
  dealId?: string | null
  trigger?: string | null
  repeatReason?: string | null
}

interface SendArgs {
  to: string;
  subject: string;
  html: string;
  text?: string;
  replyTo?: string;
  /**
   * Which deal this email belongs to, and which action sent it.
   *
   * Without these the server cannot attach a bounce to a deal, and the repeat
   * guard has nothing to key on, so a second send is never refused. Both were
   * missing, which is why every delivery row landed with a null deal.
   */
  dealId?: string | null;
  trigger?: string | null;
  repeatReason?: string | null;
}

/** POST to the server-side send function. Non-throwing: returns a result object. */
async function postEmail(args: SendArgs): Promise<EmailResult> {
  if (!args.to) {
    return { success: false, message: 'No email address provided' };
  }
  try {
    const res = await fetch('/api/send-email', {
      method: 'POST',
      headers: await apiHeadersOrThrow(),
      body: JSON.stringify(args),
    });

    // If the function isn't deployed yet, the SPA fallback returns HTML.
    const ct = res.headers.get('content-type') || '';
    if (!ct.includes('application/json')) {
      return { success: false, message: 'Email service not available (send function not deployed)' };
    }

    const data = await res.json();
    if (!res.ok || !data.success) {
      return { success: false, message: data.error || `Email send failed (${res.status})` };
    }
    return {
      success: true,
      message: `Email sent to ${args.to}`,
      details: { email: args.to, subject: args.subject, email_id: data.id },
    };
  } catch (err) {
    return { success: false, message: err instanceof Error ? err.message : 'Email send failed' };
  }
}


/**
 * Ask the server to mint a link and send one of the two account emails.
 *
 * The recovery call is deliberately unauthenticated — somebody who has forgotten their
 * password is not signed in — and the endpoint answers the same way whether or not the
 * address is known, so this never reports "no such user" either.
 */
async function accountEmail(email: string, kind: 'invite' | 'recovery', name?: string): Promise<EmailResult> {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (kind === 'invite') Object.assign(headers, await apiHeadersOrThrow())
    const res = await fetch('/api/admin/account-email', {
      method: 'POST',
      headers,
      body: JSON.stringify({ email, kind, ...(name ? { name } : {}) }),
    })
    const ct = res.headers.get('content-type') || ''
    if (!ct.includes('application/json')) {
      /* An HTML body here is the SPA's own 404 page: the Function is not deployed on this
         environment. That is the one case worth falling back for. */
      return { success: false, endpointMissing: true, message: 'The account-email function is not deployed on this environment.' }
    }
    const body = await res.json().catch(() => ({}) as Record<string, unknown>)
    if (!res.ok || body?.success === false) {
      const detail = body?.detail ? ` ${String(body.detail)}` : ''
      return {
        success: false,
        endpointMissing: res.status === 501,
        message: String(body?.error || `The email could not be sent (${res.status}).`) + detail,
      }
    }
    return { success: true, message: String(body?.message || 'Sent.') }
  } catch (e) {
    return { success: false, message: e instanceof Error ? e.message : 'The email could not be sent.' }
  }
}

export const emailService = {
  /**
   * Send the DD Collection form link to a seller.
   * Framework E: document requirements vary by seller type.
   */
  sendDD_CollectionForm: async (
    email: string,
    sellerName: string,
    sellerType: 'individual' | 'company' | 'dealer',
    sellerToken: string,
    tracking?: EmailTracking
  ): Promise<EmailResult> => {
    // Same reasoning as the Vehicle Purchase Agreement SMS: an email whose only instruction
    // is "upload here" is worse than no email when the link goes nowhere.
    if (!sellerToken) {
      return {
        success: false,
        message: 'This deal has no seller link token yet - run migration 025. Nothing was sent.',
      };
    }
    const subject = `Due Diligence Documents Needed - ${sellerName}`;
    const html = constructDD_CollectionEmail(sellerName, sellerType, sellerToken);
    return postEmail({ to: email, subject, html , ...(tracking || {}) });
  },

  /**
   * Send an invoice to a buyer.
   */
  sendInvoice: async (
    email: string,
    buyerName: string,
    invoiceNumber: string,
    totalAmount: number,
    dueDate: string,
    tracking?: EmailTracking
  ): Promise<EmailResult> => {
    const subject = `Invoice ${invoiceNumber} - Direct Auto`;
    const html = constructInvoiceEmail(buyerName, invoiceNumber, totalAmount, dueDate);
    return postEmail({ to: email, subject, html , ...(tracking || {}) });
  },

  /**
   * Seller notification when a deal / Vehicle Purchase Agreement is created.
   */
  sendDealNotification: async (
    sellerEmail: string,
    sellerName: string,
    vehicle: { year: number; make: string; model: string; rego?: string },
    dealLink: string,
    tracking?: EmailTracking
  ): Promise<EmailResult> => {
    const veh = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
    const html = `
      <h2>Your Vehicle Deal at Direct Auto</h2>
      <p>Hi ${sellerName},</p>
      <p>Your vehicle offer is ready:</p>
      <ul>
        <li><strong>${veh}</strong></li>
        ${vehicle.rego ? `<li>Registration: <strong>${vehicle.rego}</strong></li>` : ''}
      </ul>
      <p><a href="${dealLink}" style="background:#d6ff3f;color:#0a0d0f;padding:12px 24px;text-decoration:none;border-radius:6px;font-weight:bold;">View Deal &amp; Sign</a></p>
      <p>Questions? Please contact your sales representative.</p>
      <p>—<br>Direct Auto</p>`;
    return postEmail({ to: sellerEmail, subject: `Vehicle Purchase Agreement Ready - ${veh}`, html , ...(tracking || {}) });
  },

  /**
   * AR payment reminder.
   */
  sendPaymentReminder: async (
    buyerEmail: string,
    buyerName: string,
    invoiceNumber: string,
    dueAmount: number,
    dueDate: string,
    paymentLink: string
  ): Promise<EmailResult> => {
    const html = `
      <h2>Payment Reminder</h2>
      <p>Hi ${buyerName},</p>
      <p>A friendly reminder that payment is due.</p>
      <p><strong>Invoice:</strong> ${invoiceNumber}<br/>
      <strong>Amount Due:</strong> $${dueAmount.toLocaleString('en-AU')}<br/>
      <strong>Due Date:</strong> ${dueDate}</p>
      <p><a href="${paymentLink}" style="background:#d6ff3f;color:#0a0d0f;padding:12px 24px;text-decoration:none;border-radius:6px;font-weight:bold;">Pay Now</a></p>
      <p>If you've already paid, please disregard this message.</p>
      <p>—<br>Direct Auto Finance</p>`;
    return postEmail({ to: buyerEmail, subject: `Payment Due - ${invoiceNumber}`, html });
  },

  /**
   * Overdue payment demand.
   */
  sendDemandLetter: async (
    buyerEmail: string,
    buyerName: string,
    invoiceNumber: string,
    daysOverdue: number,
    outstandingAmount: number
  ): Promise<EmailResult> => {
    const html = `
      <h2>Payment Demand Notice</h2>
      <p>Hi ${buyerName},</p>
      <p>Your invoice is now <strong>${daysOverdue} days overdue</strong>.</p>
      <p><strong>Invoice:</strong> ${invoiceNumber}<br/>
      <strong>Outstanding:</strong> $${outstandingAmount.toLocaleString('en-AU')}</p>
      <p>Payment is now required immediately. If payment is not received within 7 days we will pursue recovery action.</p>
      <p>—<br>Direct Auto Finance</p>`;
    return postEmail({ to: buyerEmail, subject: `URGENT: Payment Demand - ${invoiceNumber}`, html });
  },

  /**
   * Notify the 2nd payment approver that a run is ready for sign-off.
   * Framework Part D: two-person payment authorisation.
   */
  sendPaymentApprovalNotification: async (
    approverEmail: string,
    info: { vehicle: string; sellerName: string; totalAmount: number; paymentRunLink: string }
  ): Promise<EmailResult> => {
    const amount = `$${info.totalAmount.toLocaleString('en-AU', { minimumFractionDigits: 2 })}`;
    const html = `
      <h2>Payment approval needed</h2>
      <p>A payment run has passed the 1st approval and is ready for your final sign-off before release to Westpac.</p>
      <p><strong>Vehicle:</strong> ${info.vehicle}<br/>
      <strong>Seller:</strong> ${info.sellerName}<br/>
      <strong>Total to pay:</strong> ${amount}</p>
      <p><a href="${info.paymentRunLink}" style="background:#d6ff3f;color:#0a0d0f;padding:12px 24px;text-decoration:none;border-radius:6px;font-weight:bold;">Review &amp; Approve</a></p>
      <p>Payment cannot be released until you approve.</p>
      <p>—<br>Direct Auto</p>`;
    return postEmail({ to: approverEmail, subject: `Payment approval needed — ${info.vehicle}`, html });
  },

  /**
   * First-time setup, and password recovery — two different emails for two different events.
   *
   * Winnie, 18 September: "There should be two different 'Invite user' template and a
   * 'Recovery' (reset) template." Both templates live server-side in
   * functions/api/_lib/accountEmails.js, and so does the link-minting, because generating an
   * action link needs the service role and the recovery path has no signed-in caller at all.
   * These two are the thin callers, so the app keeps one obvious place to ask from.
   */
  sendInvite: async (email: string, name?: string): Promise<EmailResult> => accountEmail(email, 'invite', name),

  sendPasswordRecovery: async (email: string): Promise<EmailResult> => accountEmail(email, 'recovery'),

  /**
   * Generic send (used for tests / ad-hoc).
   */
  send: async (
      to: string,
      subject: string,
      html: string,
      text?: string,
      tracking?: { dealId?: string | null; trigger?: string | null; repeatReason?: string | null }
    ): Promise<EmailResult> => {
    return postEmail({ to, subject, html, text, ...(tracking || {}) });
  },
};

/**
 * The app's public base URL.
 *
 * This was hard-coded to https://app.directauto.net.au, so every link the
 * platform emailed pointed at production — including from staging, where it sent
 * approvers to a different environment's data. VITE_APP_URL is already set per
 * Cloudflare Pages project; fall back to wherever the app is actually running.
 */
export const appBaseUrl = (): string => {
  const configured = import.meta.env.VITE_APP_URL as string | undefined
  if (configured) return configured.replace(/\/+$/, '')
  if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin
  return 'https://app.directauto.net.au'
}

/**
 * Construct the DD Collection email HTML.
 */
function constructDD_CollectionEmail(
  sellerName: string,
  sellerType: 'individual' | 'company' | 'dealer',
  sellerToken: string
): string {
  // Was `${appBaseUrl()}/upload-dd/${dealId}` - a path that was never a route,
  // so every seller who followed it got the DirectAuto login screen and their
  // documents never arrived. It is now the seller portal, keyed on the token.
  const ddLink = `${appBaseUrl()}/seller/${sellerToken}`;
  const documentsList = getDocumentsBySellerType(sellerType);

  return `
    <h2>Due Diligence Documents Needed</h2>
    <p>Hi ${sellerName},</p>
    <p>Thank you for signing the Vehicle Purchase Agreement. We now need your due diligence documents to proceed.</p>
    <p><strong>Upload here (7-day deadline):</strong><br/>
    <a href="${ddLink}">${ddLink}</a></p>
    <h3>Required Documents:</h3>
    <ul>
      ${documentsList.map((doc) => `<li>${doc}</li>`).join('')}
    </ul>
    <p>Best regards,<br/>Direct Auto Team</p>`;
}

/**
 * Construct the Invoice email HTML.
 */
function constructInvoiceEmail(
  buyerName: string,
  invoiceNumber: string,
  totalAmount: number,
  dueDate: string
): string {
  const formattedAmount = `$${totalAmount.toLocaleString('en-AU', { minimumFractionDigits: 2 })}`;
  return `
    <h2>Invoice ${invoiceNumber}</h2>
    <p>Hi ${buyerName},</p>
    <p>Thank you for your purchase. Please find your invoice below.</p>
    <table border="1" cellpadding="10" style="border-collapse: collapse;">
      <tr><th>Invoice Number</th><th>Total Amount Due</th><th>Due Date</th></tr>
      <tr><td>${invoiceNumber}</td><td>${formattedAmount}</td><td>${dueDate}</td></tr>
    </table>
    <h3>Payment Instructions:</h3>
    <p>Please transfer payment to the bank account details provided in your original contract.</p>
    <p>Best regards,<br/>Direct Auto Team</p>`;
}

/**
 * Document requirements by seller type (Framework E).
 */
function getDocumentsBySellerType(sellerType: string): string[] {
  const docs: Record<string, string[]> = {
    individual: [
      'Driver Licence (front)',
      'Driver Licence (back)',
      'Selfie ID (hold licence, face visible)',
      'Registration Certificate',
      'Bank Account Details',
      'Finance Payout Letter (if applicable)',
      'PPSR Search Certificate (auto-generated)',
      'ABR Lookup (auto-generated)',
    ],
    company: [
      'Company Vehicle Registration Certificate',
      'Full Company Extract with Director details',
      'Director ID (front & back - all directors)',
      'Selfie ID (each director)',
      'Tax Invoice',
      'Company Bank Account Details',
      'PPSR Search Certificate (auto-generated)',
      'ABR Lookup (auto-generated)',
    ],
    dealer: [
      'Tax Invoice',
      'PPSR Certificate',
      'Dealer Bank Account Details',
      'Auction Trust Account (if applicable)',
      'LMCT/MDL Licence (if applicable)',
    ],
    // Types 4 and 5 were missing, so an auction or tender seller was emailed an empty
    // list. Kept in step with requiredDocuments in functions/api/_lib/sellerPortal.js.
    auction: [
      'Tax Invoice',
      'PPSR Certificate',
      'Auction House Bank Details',
      'Auction Trust Account',
      'LMCT/MDL Licence (if applicable)',
    ],
    tender: [
      'Tax Invoice',
      'PPSR Certificate',
      'Seller Bank Details',
      'LMCT/MDL Licence (if applicable)',
    ],
  };
  return docs[String(sellerType || '').toLowerCase()] || [];
}
